// src/config.js
// Get API base URL from environment variable or use default
const getApiBaseUrl = () => {
  // Vite environment variable (used in build)
  if (import.meta.env.VITE_API_BASE_URL && import.meta.env.PROD) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  
  // Development: use relative path to leverage Vite proxy
  if (!import.meta.env.PROD) {
    return '/api';
  }
  
  // Production fallback: construct from environment variables
  const host = import.meta.env.VITE_API_HOST || 'localhost';
  const port = import.meta.env.VITE_API_PORT || '5000';
  const protocol = import.meta.env.VITE_API_PROTOCOL || 'http';
  return `${protocol}://${host}:${port}/api`;
};

export const API_CONFIG = {
  BASE_URL: getApiBaseUrl(),
  ENDPOINTS: {
    // Auth
    REGISTER: '/auth/register',
    LOGIN: '/auth/login',
    PROFILE: '/auth/profile',
    CHANGE_PASSWORD: '/auth/change-password',
    
    // Account Settings
    ACCOUNT_PROFILE: '/account/profile',
    ACCOUNT_PROFILE_UPDATE: '/account/profile/update',
    ACCOUNT_COMPANY_UPDATE: '/account/company/update',
    ACCOUNT_CHANGE_PASSWORD: '/account/change-password',
    ACCOUNT_NOTIFICATIONS_UPDATE: '/account/notifications/update',
    ACCOUNT_EXPORT_DATA: '/account/export-data',
    ACCOUNT_DEACTIVATE: '/account/deactivate',
    
    // Bookings
    BOOKINGS: '/bookings',
    
    // Wallet
    WALLET_BALANCE: '/customer/wallet/balance',
    WALLET_TRANSACTIONS: '/customer/wallet/transactions',
    
    // Services
    SERVICES: '/services',
    SERVICE_CATEGORIES: '/services/categories',
    PROPERTY_TYPES: '/property-types',
    SECTORS: '/sectors',
    
    // Contact
    CONTACT: '/contact',
    CONTACT_INFO: '/contact-info',
    
    // Quotes
    QUOTES: '/quotes',
    CALCULATE_QUOTE: '/quotes/calculate',
    SERVICE_REQUESTS: '/service-requests',
    EVENT_VENUE_QUOTES: '/event-venue-quotes',
    HMO_QUOTES: '/hmo-quotes',
    SPECIALIST_CLEANING_QUOTES: '/specialist-quotes',
    
    // Payments
    PAYMENTS: '/payments',
    PAYMENT_INTENT: '/payments/create-intent',
    BANK_TRANSFER: '/payments/bank-transfer',
    
    // Database (for debugging)
    DATABASE_INFO: '/database/info',
    HEALTH: '/health',

    // ===== MVP FEATURES =====
    
    // Shopping Cart / Basket (Ecommerce Flow)
    CART: '/cart',
    CART_ADD: '/cart/add',
    CART_UPDATE: '/cart/item/:id',
    CART_REMOVE: '/cart/item/:id',
    CART_CHECKOUT: '/cart/checkout',
    
    // SEO Landing Pages
    SEO_PAGES: '/seo/landing-pages',
    SEO_PAGE: '/seo/page/:slug',
    ADMIN_SEO_CREATE: '/admin/seo/pages',
    
    // Website Pages (Database-driven)
    PUBLIC_PAGE: '/public/pages/:slug',
    
    // Organisations / B2B
    ORGANISATIONS: '/organisations',
    ORGANISATION_DETAIL: '/organisations/:id',
    ORGANISATION_MEMBERS: '/organisations/:id/members',
    ORGANISATION_PROPERTIES: '/organisations/:id/properties',
    
    // Provider Earnings & Payouts
    PROVIDER_EARNINGS: '/provider/earnings',
    PROVIDER_EARNINGS_STATEMENT: '/provider/earnings/statement/:month/:year',
    PROVIDER_EARNINGS_CSV: '/provider/earnings/download/csv/:month/:year',
    PROVIDER_PAYOUTS: '/provider/payouts',
    PROVIDER_PAYOUT_SUBMIT: '/provider/payouts/request',
    
    // Admin Price Management
    ADMIN_SERVICE_PRICE: '/admin/services/:id/price',
    ADMIN_PRICE_HISTORY: '/admin/services/:id/price-history',
    ADMIN_BULK_PRICE_UPDATE: '/admin/services/bulk-price-update',
    
    // Admin Billing & Revenue
    ADMIN_BILLING_DATA: '/admin/billing-data'
  }
};

// Get full URL for API endpoint
export const getApiUrl = (endpoint) => {
  const base = API_CONFIG.BASE_URL; // Already includes /api (e.g., http://localhost:5000/api)
  
  // Ensure endpoint starts with /
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  // CRITICAL GUARD: Prevent /api/api double prefix
  if (path.startsWith('/api')) {
    console.error('🚨 [API GUARD] Double /api prefix detected!');
    console.error('   Input:', endpoint);
    console.error('   Base URL already includes /api:', base);
    console.error('   Correct usage: Use', path.replace(/^\/api/, ''), 'instead');
    // Auto-fix: Remove the /api prefix
    return `${base}${path.replace(/^\/api/, '')}`;
  }
  
  return `${base}${path}`;
};

// Export base URL directly (without /api suffix) for flexibility
export const getApiBaseUrlWithoutPath = () => {
  const url = API_CONFIG.BASE_URL;
  return url.replace(/\/api$/, ''); // Remove /api suffix if present
};